import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOMeta from '@/components/feature/SEOMeta';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);
  const [shareToast, setShareToast] = useState(false);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-24 px-4 md:px-8 lg:px-12 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-900 mb-4">文章不存在</h1>
            <Link to="/" className="text-accent-500 hover:underline">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.id === article.categoryId);
  const relatedArticlesList = articles.filter(
    (a) => article.relatedArticles.includes(a.id) && a.id !== article.id
  );

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: category?.name || '分类', href: `/category/${category?.slug}` },
    { label: article.title },
  ];

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: article.title,
        text: article.excerpt,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      setShareToast(true);
      setTimeout(() => setShareToast(false), 2000);
    }
  };

  const articleStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    datePublished: article.publishedAt,
    dateModified: article.publishedAt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '五一吃瓜网',
      logo: {
        '@type': 'ImageObject',
        url: 'http://xjqkfyv.cn/logo.png',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `http://xjqkfyv.cn/article/${article.slug}`,
    },
    articleSection: article.categoryName,
    keywords: article.tags.join(','),
  };

  const webpageStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: article.title,
    url: `http://xjqkfyv.cn/article/${article.slug}`,
    description: article.excerpt,
    publisher: {
      '@type': 'Organization',
      name: '五一吃瓜网',
    },
    datePublished: article.publishedAt,
    dateModified: article.publishedAt,
  };

  return (
    <>
      <SEOMeta
        title={`${article.title} - 五一吃瓜网`}
        description={article.excerpt}
        keywords={`${article.tags.join(',')},五一吃瓜网,吃瓜不打烊`}
        canonical={`http://xjqkfyv.cn/article/${article.slug}`}
        ogType="article"
        ogImage={article.coverImage}
        articlePublishedTime={article.publishedAt}
        articleModifiedTime={article.publishedAt}
        articleAuthor={article.author}
        articleSection={article.categoryName}
        articleTags={article.tags}
        structuredData={[articleStructuredData, webpageStructuredData]}
      />

      <Navbar />

      <header className="relative w-full h-[350px] md:h-[450px] overflow-hidden mt-16 md:mt-20">
        <img
          src={article.coverImage}
          alt={article.title}
          title={`${article.title} | 五一吃瓜网`}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50" />
        <div className="absolute inset-0 flex flex-col items-center justify-end text-center px-4 pb-8 md:pb-12">
          <span className="inline-block bg-accent-500 text-background-50 text-xs font-medium px-3 py-1 rounded-full mb-3">
            {article.categoryName}
          </span>
          <h1 className="text-xl md:text-3xl lg:text-4xl font-bold text-white font-heading max-w-3xl leading-snug">
            {article.title}
          </h1>
        </div>
      </header>

      <main className="px-4 md:px-8 lg:px-12 py-8 md:py-12">
        <div className="max-w-3xl mx-auto">
          <Breadcrumb items={breadcrumbItems} />

          <div className="mt-6 flex items-center justify-between text-sm text-foreground-500">
            <div className="flex items-center gap-3 flex-wrap">
              <span>作者：{article.author}</span>
              <span className="w-1 h-1 rounded-full bg-foreground-300" />
              <span>发布时间：{article.publishedAt}</span>
              <span className="w-1 h-1 rounded-full bg-foreground-300" />
              <span>{article.readTime}阅读</span>
              <span className="w-1 h-1 rounded-full bg-foreground-300" />
              <span>{(article.views / 10000).toFixed(1)}万阅读</span>
            </div>
          </div>

          <div className="mt-8">
            <div className="relative h-64 md:h-80 rounded-2xl overflow-hidden mb-8">
              <img
                src={article.coverImage}
                alt={article.title}
                title={`${article.title} | 五一吃瓜网`}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
            </div>

            <div className="prose prose-lg max-w-none">
              <div
                className="text-foreground-800 leading-relaxed space-y-4 text-base md:text-lg"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />
            </div>

            <div className="mt-8 flex flex-wrap gap-2">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="bg-secondary-100 text-secondary-900 text-xs font-medium px-3 py-1 rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>

            <div className="mt-8 flex justify-center">
              <button
                onClick={handleShare}
                className="bg-primary-500 text-background-50 px-8 py-3 rounded-full text-sm font-medium flex items-center gap-2 hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-share-forward-line" />
                分享吃瓜
              </button>
            </div>

            {shareToast && (
              <div className="fixed bottom-8 left-1/2 -translate-x-1/2 bg-foreground-900 text-background-50 px-4 py-2 rounded-full text-sm z-50">
                链接已复制到剪贴板
              </div>
            )}
          </div>

          {relatedArticlesList.length > 0 && (
            <div className="mt-12 md:mt-16">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {relatedArticlesList.map((rel) => (
                  <article
                    key={rel.id}
                    className="group bg-background-50 rounded-xl overflow-hidden hover:shadow-md transition-all duration-300"
                  >
                    <Link to={`/article/${rel.slug}`} className="block">
                      <div className="relative h-40 overflow-hidden">
                        <img
                          src={rel.coverImage}
                          alt={rel.title}
                          title={`${rel.title} | 五一吃瓜网`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                      </div>
                      <div className="p-4">
                        <span className="inline-block bg-accent-500 text-background-50 text-xs font-medium px-2 py-0.5 rounded-md mb-2">
                          {rel.categoryName}
                        </span>
                        <h4 className="text-sm font-bold text-foreground-900 leading-snug group-hover:text-accent-500 transition-colors line-clamp-2">
                          {rel.title}
                        </h4>
                      </div>
                    </Link>
                  </article>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}